import { useState } from 'preact/hooks'
import { websApi, type WebViewDto } from '@/api/webs'
import { useToast } from '@/hooks/useToast'
import type { UseFormReturn, FormState, UseFormGetFieldState } from 'react-hook-form'
import type { OnlineEventAIForm } from '@/hooks/useOnlineEventForm'
import { refetchUnlessSentFieldsChanged, retryOnStaleETag } from '@/utils/etagRetry'

interface UseAgendaEditorParams {
    web: WebViewDto
    onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
    form: UseFormReturn<OnlineEventAIForm>
    watched: { value: string }[] | undefined
    getFieldState: UseFormGetFieldState<OnlineEventAIForm>
    formState: FormState<OnlineEventAIForm>
    setEditingAgenda: (v: boolean) => void
}

/** What an agenda save sends, as the web holds it: a 412 is retried only when it did not change meanwhile. */
const savedAgenda = (document: WebViewDto) => document.OnlineEvent?.Agenda ?? []

export function useAgendaEditor({ web, onPatchWeb, form, watched, getFieldState, formState, setEditingAgenda }: UseAgendaEditorParams) {
    const onlineEvent = web.OnlineEvent
    const { showSuccess, showError } = useToast()
    const [saving, setSaving] = useState(false)

    const hasDirtyEmpty = (watched || []).some((x, idx) => {
        const val = (x?.value ?? '').trim()
        const fstate = getFieldState(`agenda.${idx}.value`, formState)
        return fstate.isDirty && val.length === 0
    })

    const normalizedInitial = (onlineEvent?.Agenda ?? []).map(s => s.trim()).filter(s => s.length > 0)
    const normalizedCurrent = (watched || []).map(x => (x?.value || '').trim()).filter(s => s.length > 0)
    const agendaChanged = JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedCurrent)

    const saveAgenda = async () => {
        setSaving(true)
        const clean = (form.getValues().agenda || []).map((x: { value: string }) => (x?.value || '').trim()).filter((x: string) => x.length > 0)
        // A room web's recording rewrites the web in the background while it uploads and prepares (ORB-1087).
        const res = await retryOnStaleETag(
            document => websApi.updateAgenda(web.WorkspaceId, web.Id, document.ETag, clean),
            web,
            refetchUnlessSentFieldsChanged(async () => (await websApi.getById(web.WorkspaceId, web.Id)).data ?? null, web, savedAgenda),
        )
        if (!res.error && res.data) {
            onPatchWeb(prev => ({ ...prev, ...res.data }))
            showSuccess('Agenda updated', 'Your agenda has been saved.')
            setEditingAgenda(false)
        } else if (res.status !== 422) showError('Save failed', res.error || 'Unable to update agenda')
        setSaving(false)
    }

    const cancelAgendaEdit = () => {
        setEditingAgenda(false)
        const next = (onlineEvent?.Agenda ?? []).map(v => ({ value: v }))
        form.reset({ ...form.getValues(), agenda: next })
    }

    return {
        saving,
        hasDirtyEmpty,
        normalizedInitial,
        agendaChanged,
        saveAgenda,
        cancelAgendaEdit,
    }
}
